import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text'
import { RowMiddle } from 'layout/Components/Row'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import { colors } from 'theme/colors'
import { DiceGameTargetType, diceGameReturnToPlayer, multiplierMax, multiplierMin } from 'views/InhouseGame/Dice/config'
import { getDicePayoutConfig } from 'views/InhouseGame/Dice/config/utils'

import { InHouseGameNumericalInput, InhouseGameInputContainer } from 'views/InhouseGame/components/styled'

const DiceGameTargetConfig: React.FC<
  BoxProps & {
    disabled: boolean
    target: string
    onTargetChanged: (value: string) => void
    targetType: DiceGameTargetType
    setTargetType: (type: DiceGameTargetType) => void
  }
> = ({ target, onTargetChanged, targetType, disabled, setTargetType, ...props }) => {
  const { t } = useTranslation()
  const payoutConfig = useMemo(() => getDicePayoutConfig(Number(target || 0), targetType), [target, targetType])
  const [multiplier, setMultiplier] = useState(payoutConfig.multiplier?.toString() || '')
  const [winRate, setWinRate] = useState(payoutConfig.winRate.toString())

  const multiplierRef = useRef<HTMLInputElement>(null)
  const winRateRef = useRef<HTMLInputElement>(null)

  const updateState = () => {
    if (document.activeElement !== multiplierRef.current) {
      setMultiplier(payoutConfig.multiplier == null ? '' : payoutConfig.multiplier.toString())
    }
    if (document.activeElement !== winRateRef.current) {
      setWinRate(payoutConfig.winRate.toString())
    }
  }

  useEffect(() => {
    updateState()
  }, [payoutConfig])

  const hasError = payoutConfig.multiplier < multiplierMin || payoutConfig.multiplier > multiplierMax

  return (
    <Flex
      {...props}
      p={['12px', '', '', '24px']}
      pb="14px"
      alignItems="center"
      justifyContent="center"
      width="100%"
      overflow="hidden"
      borderRadius={theme.radii.small}
      background={colors.backgroundAlt5}
    >
      <RowMiddle width="100%" justifyContent="space-between" flexWrap={'wrap'}>
        <Box width={['100%', '', 'calc(33.33% - 12px)']}>
          <Text fontSize="12px" bold color="textSubtle">
            {t('Multiplier')}
          </Text>
          <InhouseGameInputContainer mt="6px" $error={hasError} $disabled={disabled}>
            <RowMiddle width="100%">
              <InHouseGameNumericalInput
                ref={multiplierRef}
                onFocus={(e: any) => {
                  e.target.select()
                }}
                aria-autocomplete="none"
                id="multiplier"
                autoFocus={false}
                tabIndex={1}
                disabled={disabled}
                value={Math.abs(Number(multiplier)) !== Infinity ? multiplier : ''}
                onBlur={() => {
                  setMultiplier(payoutConfig?.multiplier?.toString() || '')
                }}
                onValueChanged={(value) => {
                  const winRate = diceGameReturnToPlayer / Number(value)
                  const target =
                    payoutConfig.targetType === DiceGameTargetType.Over ? 100 - Number(winRate) : Number(winRate)
                  onTargetChanged(Number.isNaN(target) || !target || Number(target) === 0 ? '' : target.toFixed(2))
                  updateState()
                  setMultiplier(value)
                }}
              />
              <Box pl="4px" pr="16px" minWidth={16}>
                <Text fontSize="12px" bold color="textSubtle">
                  <Trans>x</Trans>
                </Text>
              </Box>
            </RowMiddle>
          </InhouseGameInputContainer>
          <Text fontSize="10px" height="10px" color="error" mt="4px" textAlign="right">
            {hasError &&
              (Number(payoutConfig.multiplier || 0) < multiplierMin ? (
                <Trans>Minimum is "{{ min: multiplierMin }}"</Trans>
              ) : (
                <Trans>Maximum is"{{ max: multiplierMax }}"</Trans>
              ))}
          </Text>
        </Box>
        <Box width={['calc(50% - 6px)', '', 'calc(33.33% - 12px)']} pb="14px">
          <Text fontSize="12px" bold color="textSubtle">
            {targetType === DiceGameTargetType.Over ? <Trans>Roll Over</Trans> : <Trans>Roll Under</Trans>}
          </Text>
          <StyledTargetDisplayContainer
            $disabled={disabled}
            mt="6px"
            onClick={() => {
              if (disabled) return
              const type = targetType === DiceGameTargetType.Over ? DiceGameTargetType.Under : DiceGameTargetType.Over
              const target = type === DiceGameTargetType.Over ? 100 - payoutConfig.winRate : payoutConfig.winRate
              onTargetChanged(target.toFixed(2))
              setTargetType(type)
            }}
          >
            <RowMiddle width="100%" style={{ pointerEvents: 'none' }} alignItems="center">
              <InHouseGameNumericalInput
                aria-autocomplete="none"
                id="target"
                disabled
                autoFocus={false}
                tabIndex={1}
                value={Math.abs(payoutConfig.target) !== Infinity ? payoutConfig.target : ''}
                onValueChanged={() => {}}
              />
              <Flex ml="4px" mr="16px" alignItems="center">
                <Icons.RefreshIcon stroke={colors.textSubtle} width="16px" height="16px" />
              </Flex>
            </RowMiddle>
          </StyledTargetDisplayContainer>
        </Box>
        <Box width={['calc(50% - 6px)', '', 'calc(33.33% - 12px)']} pb="14px">
          <Text fontSize="12px" bold color="textSubtle">
            <Trans>Win chance</Trans>
          </Text>

          <InhouseGameInputContainer mt="6px" $disabled={disabled}>
            <RowMiddle width="100%">
              <InHouseGameNumericalInput
                onFocus={(e: any) => {
                  e.target.select()
                }}
                ref={winRateRef}
                aria-autocomplete="none"
                id="win-chance"
                disabled={disabled}
                autoFocus={false}
                tabIndex={1}
                value={Math.abs(Number(winRate)) !== Infinity ? winRate : ''}
                onBlur={() => {
                  setWinRate(payoutConfig.winRate.toString())
                }}
                onValueChanged={(value) => {
                  const target =
                    payoutConfig.targetType === DiceGameTargetType.Over ? 100 - Number(value) : Number(value)
                  setWinRate(value)
                  updateState()
                  onTargetChanged(Number.isNaN(target) || !target || Number(target) === 0 ? '' : target.toFixed(2))
                }}
              />
              <Box pl="4px" pr="16px" minWidth={16}>
                <Text fontSize="12px" bold color="textSubtle">
                  <Trans>%</Trans>
                </Text>
              </Box>
            </RowMiddle>
          </InhouseGameInputContainer>
        </Box>
      </RowMiddle>
    </Flex>
  )
}

const StyledTargetDisplayContainer = styled(Box)<{ $disabled?: boolean }>`
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.stroke};

  display: flex;
  align-items: center;

  width: 100%;
  height: ${({ height }) => height || '40px'};

  border-color: ${({ theme }) => theme.colors.backgroundAlt8};
  border-radius: ${({ theme }) => theme.radii.tiny};
  border-width: 2px;

  background: ${({ theme }) => theme.colors.backgroundAlt9};

  cursor: pointer;

  input: {
    cursor: pointer;
  }

  ${({ $disabled }) =>
    $disabled
      ? css`
          opacity: 0.5;
          border: 2px solid ${({ theme }) => theme.colors.backgroundAlt8} !important;
          &:hover {
            border: 2px solid ${({ theme }) => theme.colors.backgroundAlt8} !important;
          }
        `
      : ''}

  &:focus-within,
  &:hover {
    border-color: ${({ theme }) => theme.colors.strokeAlt};
  }
`
export default DiceGameTargetConfig
